/**
 * Reais, em duas medidas: compacta pra caber e extensa pra conferir.
 *
 * Nasce em módulo próprio de propósito. O precedente óbvio seria
 * `formatCena`, que mora colado no `CenaGauge` — e é justamente o
 * contraexemplo: ele acabou DUPLICADO no `DashboardSummaryCards`,
 * porque quem precisou dele numa segunda tela achou mais fácil copiar
 * do que importar de um componente. Dinheiro aparece em quatro
 * superfícies (menções, resumo, relatório, dashboard); duas cópias
 * divergiriam na primeira vez que alguém mexesse numa.
 *
 * **`null` é "—", `0` é "R$ 0,00".** A distinção é o ponto: a
 * clipadora que não informou e a matéria que vale zero são coisas
 * diferentes, e a Clipei manda `0,00` de verdade. Um guard escrito
 * como `if (!value)` colapsa as duas — por isso a comparação aqui é
 * estrita, e é o mesmo cuidado que o template de referência precisou
 * ter (`if (!num && num !== 0)`).
 */

/** O que aparece no lugar de um valor ausente. Mesmo traço do resto da
 *  interface — ver `formatReach`. */
const AUSENTE = "—";

const compacto = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  notation: "compact",
  maximumFractionDigits: 1
});

const extenso = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL"
});

/**
 * "R$ 46,7 mil" — pra célula de lista, KPI e qualquer lugar onde o
 * número divide espaço com outra coisa.
 *
 * Compacto do `Intl`, e não os sufixos "K/Mi" do template da Gi: o
 * dashboard já formata alcance e interações com `notation: "compact"`
 * em pt-BR, e duas convenções de abreviação na mesma fileira de cards
 * leriam como erro.
 */
export function formatCurrencyCompact(value: number | null | undefined): string {
  if (value == null) return AUSENTE;
  return compacto.format(value);
}

/**
 * "R$ 46.716,00" — pra tooltip, `aria-label` e qualquer lugar onde a
 * pessoa quer conferir o centavo (é o número que o analista compara
 * com a planilha da clipadora).
 */
export function formatCurrencyFull(value: number | null | undefined): string {
  if (value == null) return AUSENTE;
  return extenso.format(value);
}

/**
 * Dinheiro em OUTRA moeda, com a precisão que a tela pedir — nasceu
 * pro consumo de IA, que é em dólar (decisão da dona, 2026-09-11) e
 * cujos números são pequenos demais pra duas casas: uma chamada custa
 * US$ 0,0003, e "US$ 0,00" na linha diria que foi de graça.
 *
 * Recebe STRING porque é assim que o servidor manda (`Decimal` não
 * sobrevive ao JSON); `Number()` é seguro pra exibição — só a exibição
 * arredonda, a soma foi feita no servidor.
 *
 * **Piso, nunca zero**: um valor positivo que arredondaria pra zero na
 * precisão pedida sai como "< US$ 0,01" (ou "< US$ 0,0001" na linha).
 * A tela mostra dois níveis de precisão de propósito — 2 casas no
 * agregado, 4 na linha — e nos dois o piso existe pra "zero" continuar
 * significando zero.
 *
 * `null` é "—", como nas duas funções acima: "sem custo apurado" é a
 * tela que explica, ao lado, dizendo POR QUE (sem preço cadastrado,
 * sem token, não terminou).
 */
export function formatMoney(
  value: string | number | null | undefined,
  currency: string,
  precision = 2
): string {
  if (value == null || value === "") return AUSENTE;
  const numero = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numero)) return AUSENTE;
  const formatador = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  });
  const piso = 10 ** -precision;
  if (numero > 0 && numero < piso / 2) return `< ${formatador.format(piso)}`;
  return formatador.format(numero);
}
